import { type ReactNode } from 'react';
import { Switch } from '@/components/ui/Switch';

export function ModeButton(props: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-pressed={props.active}
      className={
        'flex-1 rounded-lg px-3 py-2 text-xs transition-colors ' +
        (props.active
          ? 'bg-accent text-bg'
          : 'text-ink-dim hover:bg-white/[0.06] hover:text-ink')
      }
      style={
        props.active
          ? undefined
          : { backgroundColor: 'rgb(var(--surface-input) / var(--surface-input-alpha))' }
      }
    >
      {props.children}
    </button>
  );
}

export function SectionHeader(props: { title: string; hint?: string; number?: string }) {
  return (
    <header className="flex items-baseline gap-3">
      <div className="min-w-0">
        <h3
          className="text-base font-semibold tracking-tight text-foreground"
        >
          {props.title}
        </h3>
        {props.hint && (
          <p className="mt-0.5 text-xs leading-snug text-muted">
            {props.hint}
          </p>
        )}
      </div>
    </header>
  );
}

export function FilterToggle(props: {
  label: string;
  desc: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  badge?: string;
}) {
  return (
    <div
      data-active={props.checked}
      onClick={() => props.onChange(!props.checked)}
      className={
        'flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5 ' +
        'transition-colors hover:bg-default/50 focus-within:bg-default/40'
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className="text-sm font-medium text-foreground"
          >
            {props.label}
          </span>
          {props.badge && (
            <span
              className={
                'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] ' +
                'bg-accent/15 font-semibold uppercase text-accent'
              }
            >
              {props.badge}
            </span>
          )}
        </div>
        <div className="text-xs leading-snug text-muted">
          {props.desc}
        </div>
      </div>
      <div className="mt-0.5 shrink-0">
        <Switch
          checked={props.checked}
          onChange={props.onChange}
          label={props.label}
        />
      </div>
    </div>
  );
}

export function LevelMeter(props: { level: number; active: boolean }) {
  // 12 segments, fill proportional to level. Greens up to 70%, ambers
  // up to 90%, red beyond — same visual code as audio mixers.
  const segments = 12;
  const filled = Math.round(props.level * segments);
  return (
    <div
      role="meter"
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={props.level}
      className="flex h-2.5 gap-0.5"
    >
      {Array.from({ length: segments }).map((_, i) => {
        const on = i < filled && props.active;
        const color =
          i < segments * 0.7
            ? 'bg-success'
            : i < segments * 0.9
              ? 'bg-amber-400'
              : 'bg-red-500';
        return (
          <div
            key={i}
            className={'flex-1 rounded-sm transition-colors ' + (on ? color : '')}
            style={!on ? { backgroundColor: 'rgb(var(--surface-input) / var(--surface-input-alpha))' } : undefined}
          />
        );
      })}
    </div>
  );
}
